import { useEffect, useState } from 'react'
import {
  App as AntdApp,
  Button,
  Card,
  DatePicker,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CheckCircleOutlined, DownloadOutlined, DollarOutlined, EyeOutlined, FileTextOutlined, FilterOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'

interface Invoice {
  id: number
  invoice_no: string
  customer_id: number
  status: 'draft' | 'issued' | 'sent' | 'paid' | 'cancelled' | 'refunded' | 'written_off'
  currency: string
  subtotal_net: string
  subtotal_tax: string
  total_gross: string
  issue_date?: string
  due_date?: string
  created_at: string
  recipient_name?: string | null
  recipient_company?: string | null
  paid_at?: string | null
}

// "Bezahlt" wird im System über paid_at bestimmt (es gibt keinen Status 'paid'
// — Ledger-Prinzip), nicht über status.
const isPaid = (r: Invoice) => !!r.paid_at || r.status === 'paid'

type TaxCode = 'auto' | 'std' | 'red' | 'zero' | 'exempt'

interface DraftItem {
  description: string
  quantity: string
  unit_price: string
  tax_code: TaxCode
  tax_rate_pct: string
}

const statusColors: Record<string, string> = {
  draft: 'default',
  issued: 'blue',
  sent: 'cyan',
  paid: 'success',
  refunded: 'purple',
  cancelled: 'red',
  written_off: 'volcano',
}

const statusLabels: Record<string, string> = {
  draft: 'Entwurf',
  issued: 'Ausgestellt',
  sent: 'Versendet',
  paid: 'Bezahlt',
  refunded: 'Erstattet',
  cancelled: 'Storniert',
  written_off: 'Abgeschrieben',
}

const fmtDate = (v?: string) =>
  v ? new Date(v).toLocaleDateString('de-DE') : '—'

const isOverdue = (r: Invoice) =>
  !!r.due_date &&
  !isPaid(r) &&
  r.status !== 'cancelled' &&
  r.status !== 'draft' &&
  r.status !== 'refunded' &&
  r.status !== 'written_off' &&
  new Date(r.due_date).getTime() < Date.now()

export default function Invoices() {
  const { t } = useTranslation()
  const { message, modal } = AntdApp.useApp()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState('')
  const [searchQ, setSearchQ] = useState('')
  const [params, setParams] = useSearchParams()
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [overdueOnly, setOverdueOnly] = useState(() => params.get('overdue') === 'true')
  const [payState, setPayState] = useState<'' | 'paid' | 'open' | 'overdue'>(() => {
    const ps = params.get('pay_state')
    return ps === 'paid' || ps === 'open' || ps === 'overdue' ? ps : ''
  })
  const [openCreate, setOpenCreate] = useState(false)
  const [selectedIDs, setSelectedIDs] = useState<number[]>([])
  const presetCustomerID = params.get('customer_id')
    ? Number(params.get('customer_id'))
    : undefined

  useEffect(() => {
    if (params.get('action') === 'create' && presetCustomerID) {
      setOpenCreate(true)
      const next = new URLSearchParams(params)
      next.delete('action')
      setParams(next, { replace: true })
    }
    const openIdRaw = params.get('open_id')
    if (openIdRaw) {
      navigate(`/invoices/${openIdRaw}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const listQ = useQuery({
    queryKey: ['invoices', statusFilter, presetCustomerID, searchQ, dateFrom, dateTo, overdueOnly, payState],
    queryFn: async () => {
      const qp: Record<string, string> = {}
      if (statusFilter) qp.status = statusFilter
      if (presetCustomerID) qp.customer_id = String(presetCustomerID)
      if (searchQ.trim()) qp.q = searchQ.trim()
      if (dateFrom) qp.from = dateFrom
      if (dateTo) qp.to = dateTo
      if (overdueOnly) qp.overdue = 'true'
      if (payState) qp.pay_state = payState
      const res = await apiClient.get<{ data: { items: Invoice[] } }>(
        '/api/v1/invoices', { params: qp },
      )
      return res.data.data.items
    },
  })

  // KPI tiles aggregate over ALL matching invoices (not just the loaded page).
  type Summary = { total_count: number; paid_count: number; paid_sum: string; open_count: number; open_sum: string; overdue_count: number; overdue_sum: string }
  const summaryQ = useQuery({
    queryKey: ['invoices-summary', statusFilter, presetCustomerID, searchQ, dateFrom, dateTo, overdueOnly],
    queryFn: async () => {
      const qp: Record<string, string> = {}
      if (statusFilter) qp.status = statusFilter
      if (presetCustomerID) qp.customer_id = String(presetCustomerID)
      if (searchQ.trim()) qp.q = searchQ.trim()
      if (dateFrom) qp.from = dateFrom
      if (dateTo) qp.to = dateTo
      if (overdueOnly) qp.overdue = 'true'
      const res = await apiClient.get<{ data: Summary }>('/api/v1/invoices/summary', { params: qp })
      return res.data.data
    },
  })

  interface BulkResult { id: number; ok: boolean; error?: string }
  const bulkMarkPaid = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ data: {
        results: BulkResult[]; success_count: number; total: number
      } }>('/api/v1/invoices/bulk/mark-paid', { ids: selectedIDs })
      return res.data.data
    },
    onSuccess: (d) => {
      message.success(`${d.success_count}/${d.total} ${t('invoice.bulkPaidDone', 'Rechnungen als bezahlt markiert')}`)
      const errs = d.results.filter(r => !r.ok)
      if (errs.length > 0) {
        modal.warning({
          title: t('invoice.bulkPartial', 'Teilweise erfolgreich'),
          width: 600,
          content: <div>{errs.map(r => <div key={r.id}>#{r.id}: {r.error}</div>)}</div>,
        })
      }
      setSelectedIDs([])
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('invoice.bulkFailed', 'Bulk fehlgeschlagen')),
  })

  const bulkSendReminders = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ data: {
        results: BulkResult[]; success_count: number; total: number
      } }>('/api/v1/invoices/bulk/send-reminders', { ids: selectedIDs })
      return res.data.data
    },
    onSuccess: (d) => {
      message.success(`${d.success_count}/${d.total} ${t('invoice.bulkRemindersDone', 'Reminder-Mails eingereiht')}`)
      setSelectedIDs([])
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('invoice.bulkFailed', 'Bulk fehlgeschlagen')),
  })

  const bulkIssue = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ data: {
        results: (BulkResult & { invoice_no?: string })[]; success_count: number; total: number
      } }>('/api/v1/invoices/bulk/issue', { ids: selectedIDs })
      return res.data.data
    },
    onSuccess: (d) => {
      message.success(`${d.success_count}/${d.total} ${t('invoice.bulkIssueDone', 'Rechnungen ausgestellt')}`)
      const errs = d.results.filter(r => !r.ok)
      if (errs.length > 0) {
        modal.warning({
          title: t('invoice.bulkPartial', 'Teilweise erfolgreich'),
          width: 600,
          content: <div>{errs.map(r => <div key={r.id}>#{r.id}: {r.error}</div>)}</div>,
        })
      }
      setSelectedIDs([])
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('invoice.bulkFailed', 'Bulk fehlgeschlagen')),
  })

  const [pdfDownloading, setPDFDownloading] = useState(false)
  const bulkDownloadPDF = async () => {
    setPDFDownloading(true)
    try {
      const res = await apiClient.post('/api/v1/invoices/bulk/download-pdf',
        { ids: selectedIDs }, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'application/zip' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'rechnungen.zip'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      message.error(t('invoice.bulkPDFFailed', 'ZIP-Download fehlgeschlagen'))
    } finally {
      setPDFDownloading(false)
    }
  }

  const columns: ColumnsType<Invoice> = [
    {
      title: t('invoice.no', 'Rechn.-Nr.'),
      dataIndex: 'invoice_no',
      key: 'no',
      width: 150,
      render: (v: string, r) => (
        <Link to={`/invoices/${r.id}`}>
          <Typography.Text code className="font-semibold">{v || '— Entwurf —'}</Typography.Text>
        </Link>
      ),
    },
    {
      title: t('invoice.customer', 'Kunde'),
      key: 'customer',
      render: (_: unknown, r: Invoice) => (
        <Link to={`/customers/${r.customer_id}`} className="font-medium">
          {r.recipient_company || r.recipient_name || `#${r.customer_id}`}
        </Link>
      ),
    },
    {
      title: t('invoice.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (v: string, r: Invoice) => (
        <Tag color={isPaid(r) ? 'success' : isOverdue(r) ? 'error' : (statusColors[v] || 'default')}>
          {isPaid(r) ? 'Bezahlt' : isOverdue(r) ? 'Überfällig' : (statusLabels[v] || v)}
        </Tag>
      ),
    },
    {
      title: t('invoice.totalGross', 'Brutto'),
      key: 'gross',
      align: 'right' as const,
      width: 130,
      render: (_, r) => (
        <Typography.Text strong>
          {parseFloat(r.total_gross).toFixed(2)} {r.currency}
        </Typography.Text>
      ),
    },
    {
      title: t('invoice.issued', 'Ausgestellt'),
      dataIndex: 'issue_date',
      key: 'issued',
      width: 120,
      render: (v?: string) => fmtDate(v),
    },
    {
      title: t('invoice.due', 'Fällig'),
      dataIndex: 'due_date',
      key: 'due',
      width: 120,
      render: (v?: string, r?: Invoice) => {
        if (!v) return '—'
        return r && isOverdue(r)
          ? <Typography.Text type="danger" strong>{fmtDate(v)}</Typography.Text>
          : fmtDate(v)
      },
    },
  ]

  const renderMobileCard = (r: Invoice) => (
    <div className={`mob-card${isOverdue(r) ? ' mob-card--urgent' : ''}`}>
      <div className="flex-between items-start mb-8">
        <Link to={`/invoices/${r.id}`}>
          <Typography.Text code className="font-semibold">{r.invoice_no || '— Entwurf —'}</Typography.Text>
        </Link>
        <Tag color={isPaid(r) ? 'success' : isOverdue(r) ? 'error' : (statusColors[r.status] || 'default')} className="ml-8">
          {isPaid(r) ? 'Bezahlt' : isOverdue(r) ? 'Überfällig' : (statusLabels[r.status] || r.status)}
        </Tag>
      </div>
      <Link to={`/customers/${r.customer_id}`} className="text-sm d-block mb-6">
        {r.recipient_company || r.recipient_name || `#${r.customer_id}`}
      </Link>
      <div className="flex-between">
        <Typography.Text type="secondary" className="text-sm">
          {r.due_date ? `Fällig: ${fmtDate(r.due_date)}` : r.issue_date ? `Ausgestellt: ${fmtDate(r.issue_date)}` : ''}
        </Typography.Text>
        <Typography.Text strong style={{ color: isOverdue(r) ? '#DC2626' : undefined }}>
          {parseFloat(r.total_gross).toFixed(2)} {r.currency}
        </Typography.Text>
      </div>
    </div>
  )

  return (
    <>
      <PageHeader
        icon={<FileTextOutlined />}
        title={t('invoice.title', 'Rechnungen')} subtitle={t('invoice.subtitle', 'Alle Rechnungen, Zahlungsstatus und Mahnwesen.')}
        extra={
          <Space wrap>
            <Button icon={<EyeOutlined />} onClick={async () => {
              try {
                const res = await apiClient.get('/api/v1/invoices/template-preview.pdf', { responseType: 'blob' })
                const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'application/pdf' }))
                window.open(url, '_blank')
                setTimeout(() => URL.revokeObjectURL(url), 60000)
              } catch {
                message.error(t('invoice.previewFailed', 'Vorschau fehlgeschlagen'))
              }
            }}>
              {t('invoice.templatePreview', 'Rechnungs-Vorschau')}
            </Button>
            <Button
              href={`/api/v1/invoices/export.csv${statusFilter ? '?status=' + statusFilter : ''}`}
            >
              {t('invoice.exportCsv', 'CSV-Export')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenCreate(true)}>
              {t('invoice.create', 'Rechnung anlegen')}
            </Button>
          </Space>
        }
      />
      <Card className="content-card mb-12">
        <Space wrap align="center">
          <FilterOutlined style={{ color: '#6b7280' }} />
          <Input.Search
            placeholder="Rechn.-Nr. suchen"
            allowClear
            className="min-w-200"
            onSearch={(v) => setSearchQ(v)}
            onChange={(e) => { if (!e.target.value) setSearchQ('') }}
          />
          <Select
            allowClear
            placeholder={t('invoice.statusFilter', 'Status')}
            className="min-w-160"
            options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))}
            onChange={(v) => setStatusFilter(v ?? '')}
          />
          <DatePicker.RangePicker
            placeholder={['Von (Ausgestellt)', 'Bis']}
            format="DD.MM.YYYY"
            onChange={(vals: [Dayjs | null, Dayjs | null] | null) => {
              setDateFrom(vals?.[0]?.format('YYYY-MM-DD') ?? '')
              setDateTo(vals?.[1]?.format('YYYY-MM-DD') ?? '')
            }}
          />
          <Space align="center">
            <Switch
              size="small"
              checked={overdueOnly}
              onChange={setOverdueOnly}
            />
            <Typography.Text style={{ color: overdueOnly ? '#dc2626' : undefined }}>
              <WarningOutlined className="mr-4" />Nur Überfällige
            </Typography.Text>
          </Space>
        </Space>
      </Card>
      {(() => {
        const rows = listQ.data || []
        const sm = summaryQ.data
        const eur = (s?: string) => `${parseFloat(s || '0').toFixed(2)} €`
        const toggle = (s: 'paid' | 'open' | 'overdue') => setPayState(prev => prev === s ? '' : s)
        const ring = (active: boolean) => active
          ? { cursor: 'pointer', outline: '2px solid #6366F1', outlineOffset: '-1px' }
          : { cursor: 'pointer' }
        return (
          <div className="kpi-grid kpi-grid-4 mb-16">
            <div className="kpi-tile" style={ring(payState === '')} onClick={() => setPayState('')}
              role="button" title="Alle anzeigen">
              <span className="kpi-tile-label"><FileTextOutlined /> Rechnungen</span>
              <span className="kpi-tile-value">{sm ? sm.total_count : rows.length}</span>
              <span className="kpi-tile-sub">{statusFilter ? (statusLabels[statusFilter] || statusFilter) : 'Alle Status'}</span>
            </div>
            <div className={`kpi-tile ${(sm?.paid_count ?? 0) > 0 ? 'kpi-success' : ''}`}
              style={ring(payState === 'paid')} onClick={() => toggle('paid')} role="button" title="Nach Bezahlt filtern">
              <span className="kpi-tile-label"><CheckCircleOutlined /> Bezahlt</span>
              <span className="kpi-tile-value">{sm ? sm.paid_count : '—'}</span>
              <span className="kpi-tile-sub">{eur(sm?.paid_sum)}</span>
            </div>
            <div className={`kpi-tile ${(sm?.open_count ?? 0) > 0 ? 'kpi-info' : ''}`}
              style={ring(payState === 'open')} onClick={() => toggle('open')} role="button" title="Nach Offen filtern">
              <span className="kpi-tile-label"><DollarOutlined /> Offen</span>
              <span className="kpi-tile-value">{sm ? sm.open_count : '—'}</span>
              <span className="kpi-tile-sub">{eur(sm?.open_sum)}</span>
            </div>
            <div className={`kpi-tile ${(sm?.overdue_count ?? 0) > 0 ? 'kpi-danger' : ''}`}
              style={ring(payState === 'overdue')} onClick={() => toggle('overdue')} role="button" title="Nach Überfällig filtern">
              <span className="kpi-tile-label"><WarningOutlined /> Überfällig</span>
              <span className="kpi-tile-value">{sm ? sm.overdue_count : '—'}</span>
              <span className="kpi-tile-sub">{(sm?.overdue_count ?? 0) > 0 ? eur(sm?.overdue_sum) : 'Keine'}</span>
            </div>
          </div>
        )
      })()}
      {selectedIDs.length > 0 && (
        <Card className="content-card mb-12">
          <Space wrap>
            <Typography.Text strong>
              {selectedIDs.length} {t('invoice.bulkSelected', 'ausgewählt')}
            </Typography.Text>
            <Popconfirm
              title={t('invoice.bulkIssueConfirm',
                `${selectedIDs.length} Entwürfe ausstellen (Datum: heute, Fälligkeit: +${14} Tage)?`)}
              onConfirm={() => bulkIssue.mutate()}
            >
              <Button type="primary" loading={bulkIssue.isPending}>
                {t('invoice.bulkIssueBtn', 'Ausstellen')}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={t('invoice.bulkPaidConfirm',
                `${selectedIDs.length} Rechnungen als manuell bezahlt markieren?`)}
              onConfirm={() => bulkMarkPaid.mutate()}
            >
              <Button loading={bulkMarkPaid.isPending}>
                {t('invoice.bulkPaidBtn', 'Als bezahlt markieren')}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={t('invoice.bulkRemindersConfirm',
                `${selectedIDs.length} Erinnerungs-Mails verschicken?`)}
              onConfirm={() => bulkSendReminders.mutate()}
            >
              <Button loading={bulkSendReminders.isPending}>
                {t('invoice.bulkRemindersBtn', 'Reminder versenden')}
              </Button>
            </Popconfirm>
            <Button icon={<DownloadOutlined />} loading={pdfDownloading} onClick={bulkDownloadPDF}>
              {t('invoice.bulkPDFBtn', 'PDFs als ZIP')}
            </Button>
            <Button type="text" onClick={() => setSelectedIDs([])}>
              {t('common.clear', 'Auswahl leeren')}
            </Button>
          </Space>
        </Card>
      )}
      <Card className="content-card">
        <DataTable<Invoice>
          rowKey="id"
          columns={columns}
          dataSource={listQ.data || []}
          loading={listQ.isLoading}
          searchPlaceholder={t('invoice.searchPlaceholder', 'Suche Rechn.-Nr. oder Kunde')}
          renderMobileCard={renderMobileCard}
          rowSelection={{
            selectedRowKeys: selectedIDs,
            onChange: (keys) => setSelectedIDs(keys as number[]),
            preserveSelectedRowKeys: true,
          }}
        />
      </Card>
      <CreateInvoiceModal
        open={openCreate}
        presetCustomerID={presetCustomerID}
        onClose={() => setOpenCreate(false)}
      />
    </>
  )
}

function CreateInvoiceModal({
  open,
  onClose,
  presetCustomerID,
}: {
  open: boolean
  onClose: () => void
  presetCustomerID?: number
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const screens = Grid.useBreakpoint()
  const [form] = Form.useForm()
  const [items, setItems] = useState<DraftItem[]>([
    { description: '', quantity: '1', unit_price: '0.00', tax_code: 'auto', tax_rate_pct: '' },
  ])

  const customersQ = useQuery({
    enabled: open,
    queryKey: ['customers-for-invoice'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: { id: number; customer_no: string; company?: string; email?: string }[] } }>(
        '/api/v1/customers?limit=200',
      )
      return res.data.data.items
    },
  })

  const create = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const payloadItems = items.map((it) =>
        it.tax_code === 'auto' ? { ...it, tax_code: '', tax_rate_pct: '' } : it,
      )
      await apiClient.post('/api/v1/invoices', { ...values, items: payloadItems })
    },
    onSuccess: () => {
      message.success(t('invoice.draftCreated', 'Draft angelegt'))
      qc.invalidateQueries({ queryKey: ['invoices'] })
      onClose()
      form.resetFields()
      setItems([{ description: '', quantity: '1', unit_price: '0.00', tax_code: 'auto', tax_rate_pct: '' }])
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('common.saveFailed', 'Speichern fehlgeschlagen')),
  })

  const updateItem = (idx: number, patch: Partial<DraftItem>) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, ...patch } : it))
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('invoice.createTitle', 'Rechnung anlegen (Draft)')}
      onOk={() => form.submit()}
      okButtonProps={{ loading: create.isPending }}
      width={screens.md ? 720 : '95vw'}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ currency: 'EUR', customer_id: presetCustomerID }}
        onFinish={(v) => create.mutate(v)}
      >
        <Form.Item name="customer_id" label={t('invoice.customer', 'Kunde')}
          rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={(customersQ.data || []).map(c => ({
              value: c.id,
              label: `${c.customer_no} — ${c.company || c.email || '?'}`,
            }))}
          />
        </Form.Item>
        <Form.Item name="currency" label={t('invoice.currency', 'Währung')} rules={[{ required: true }]}>
          <Input maxLength={3} />
        </Form.Item>
        <Typography.Title level={5} className="mb-8">{t('invoice.items', 'Positionen')}</Typography.Title>
        {items.map((it, idx) => (
          <Space key={idx} className="mb-8" align="start" wrap>
            <Input
              placeholder={t('invoice.itemDesc', 'Beschreibung')}
              value={it.description}
              onChange={(e) => updateItem(idx, { description: e.target.value })}
              className="min-w-200"
            />
            <InputNumber
              placeholder={t('invoice.qty', 'Menge')}
              value={Number(it.quantity)}
              onChange={(v) => updateItem(idx, { quantity: String(v ?? '') })}
              min={0}
              className="w-100"
            />
            <Input
              placeholder={t('invoice.unitPrice', 'Stückpreis')}
              value={it.unit_price}
              onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
              className="w-120"
            />
            <Select
              value={it.tax_code}
              onChange={(v: TaxCode) => updateItem(idx, {
                tax_code: v,
                tax_rate_pct: v === 'auto' ? '' : v === 'std' ? '19.000' : v === 'red' ? '7.000' : '0.000',
              })}
              options={[
                { value: 'auto', label: t('invoice.taxAuto', 'auto (resolve)') },
                { value: 'std', label: 'std (19%)' },
                { value: 'red', label: 'red (7%)' },
                { value: 'zero', label: 'zero' },
                { value: 'exempt', label: 'exempt' },
              ]}
              className="w-110"
            />
            <Button danger onClick={() => setItems(items.filter((_, i) => i !== idx))}
              disabled={items.length === 1}>
              –
            </Button>
          </Space>
        ))}
        <Typography.Paragraph type="secondary" className="mt-8 mb-8 text-sm">
          {t('invoice.taxAutoHint',
            '"auto" lässt den Backend-Resolver basierend auf Kunde + Produkt entscheiden.')}
        </Typography.Paragraph>
        <Button
          onClick={() => setItems([...items, { description: '', quantity: '1', unit_price: '0.00', tax_code: 'auto', tax_rate_pct: '' }])}
        >
          + {t('invoice.addItem', 'Position hinzufügen')}
        </Button>
      </Form>
    </Modal>
  )
}
